import React from 'react';
import { Box, Modal, Typography } from '@mui/material';
import { Copy, ChevronRight, X } from 'lucide-react';
import { TargetExplorer } from './TargetExplorer';
import { DiffViewer } from './DiffViewer';
import { ActionBar } from './ActionBar';
import { usePlanner } from './usePlanner';
import type { TargetTemplateInfo, EditedConfigInfo, TargetWithConfigs } from './types';

export interface CopyPlannerProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (selections: TargetWithConfigs[]) => Promise<void>;
  targetTemplates: TargetTemplateInfo[];
  editedConfigs: EditedConfigInfo[];
  isCopying?: boolean;
  sourceFuelType?: string;
  sourceSection?: string;
  sourceTemplateName?: string;
}

export const CopyPlanner: React.FC<CopyPlannerProps> = ({
  open,
  onClose,
  onConfirm,
  targetTemplates,
  editedConfigs,
  isCopying = false,
  sourceFuelType = '',
  sourceSection,
  sourceTemplateName,
}) => {
  const planner = usePlanner({ targetTemplates, editedConfigs, sourceFuelType, open });

  return (
    <Modal open={open} onClose={isCopying ? undefined : onClose} keepMounted={false}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'min(90vw, 1200px)',
          height: '75vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          borderRadius: '12px',
          border: '1px solid #dedede',
          boxShadow: '0 25px 50px rgba(0,0,0,0.15)',
          overflow: 'hidden',
          outline: 'none',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                p: '6px',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(0,51,102,0.08)',
              }}
            >
              <Copy size={14} color="#003366" />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>Copy Planner</Typography>
            {sourceTemplateName && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ fontFamily: 'monospace', color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase' }}
                >
                  {sourceTemplateName}
                </Typography>
                <ChevronRight size={12} color="#94a3b8" />
                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem' }}>
                  {sourceSection ?? 'Section'}
                </Typography>
              </Box>
            )}
            {!sourceTemplateName && (
              <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', ml: 1 }}>
                {sourceSection ?? 'Section'}
              </Typography>
            )}
          </Box>
          <button
            onClick={isCopying ? undefined : onClose}
            disabled={isCopying}
            style={{ background: 'none', border: 'none', cursor: isCopying ? 'default' : 'pointer', color: '#94a3b8', display: 'flex', alignItems: 'center', padding: 4, borderRadius: 4 }}
          >
            <X size={18} />
          </button>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '380px 1fr',
            minHeight: 0,
            flex: 1,
          }}
        >
          <TargetExplorer
            targets={targetTemplates}
            editedConfigs={editedConfigs}
            planner={planner}
          />
          <DiffViewer
            targets={targetTemplates}
            editedConfigs={editedConfigs}
            planner={planner}
          />
        </Box>

        <ActionBar
          planner={planner}
          targets={targetTemplates}
          isCopying={isCopying}
          onCancel={onClose}
          onConfirm={async () => {
            const selections = planner.toSelections(targetTemplates);
            await onConfirm(selections);
          }}
        />
      </Box>
    </Modal>
  );
};

export default CopyPlanner;
